import React, { useState } from 'react';
import {
    Trash2,
    CheckCircle,
    AlertCircle,
    ArrowRight,
    Database,
    Zap,
    Activity,
    Edit2,
    Square,
    X,
    Shuffle,
    Plus,
    Sparkles,
    RefreshCw,
    Network,
    ShieldCheck,
    AlertTriangle,
    Globe,
    Search,
    SlidersHorizontal,
    ArrowUpDown,
    ChevronDown,
    Check,
    Save
} from 'lucide-react';
import awsLogo from '../../assets/logos/aws-logo.svg';
import gcpLogo from '../../images/gcp-logo.png';
import azureLogo from '../../assets/logos/azure-logo.svg';
import onPremiseLogo from '../../assets/logos/on-premises-logo.svg';
import './DMSWizard.css';

import { API_BASE_URL } from '../../config';
import MigrationDetailsModal from './MigrationDetailsModal';

const DMSMigrations = ({
    jobs,
    connections,
    selectedJobs,
    toggleJobSelection,
    handleSelectMultipleJobs,
    handleCutover,
    isWizardOpen,
    setIsWizardOpen,
    step,
    setStep,
    formData,
    setFormData,
    loading,
    validationResult,
    testSteps = { source: 'pending', target: 'pending', bridge: 'pending' },
    handleValidateConnection,
    handleStartMigration,
    onCreateConnection,
    onDeleteJobs,
    onEditDraft,
    editContext,
    fetchJobs,
    showNotification,
    serverIP,
    onNavigateToSettings,
    vpnStatus = { loading: false, configured: false }
}) => {

    // Local state for tabs and toolbar
    const [activeTab, setActiveTab] = useState('jobs');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [sortBy, setSortBy] = useState('recently_created');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const [cutoverModalOpen, setCutoverModalOpen] = useState(false);
    const [cutoverJobId, setCutoverJobId] = useState(null);
    const [stopModalOpen, setStopModalOpen] = useState(false);
    const [stopJobId, setStopJobId] = useState(null);
    const [selectedJobForModal, setSelectedJobForModal] = useState(null);

    // Close dropdowns on outside clicks
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.toolbar-dropdown-container')) {
                setIsFilterDropdownOpen(false);
                setIsSortDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleTabChange = (tab) => {
        if (tab === activeTab) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setActiveTab(tab);
            setSearchQuery('');
            setSelectedFilter('all');
            setIsTransitioning(false);
        }, 300);
    };

    const ProviderLogo = ({ provider, size = 16, className = "" }) => {
        const p = provider?.toLowerCase();
        if (p === 'aws') return <img src={awsLogo} alt="AWS" width={Math.round(size * 1.6)} height={size} className={`${className} aws-logo-theme-fix`} style={{ objectFit: 'contain', verticalAlign: 'middle' }} />;
        if (p === 'gcp') return <img src={gcpLogo} alt="GCP" width={size} height={size} className={className} style={{ objectFit: 'contain', verticalAlign: 'middle' }} />;
        if (p === 'azure') return <img src={azureLogo} alt="Azure" width={size} height={size} className={className} style={{ objectFit: 'contain', verticalAlign: 'middle' }} />;
        if (p === 'on-premise') return <img src={onPremiseLogo} alt="On-Premise" width={size} height={size} className={className} style={{ objectFit: 'contain', verticalAlign: 'middle' }} />;
        return <Globe size={size} style={{ verticalAlign: 'middle' }} />;
    };


    // Multi-Database Discovery State
    const [discoveredDbs, setDiscoveredDbs] = useState([]);
    const [selectedDatabases, setSelectedDatabases] = useState([]);
    const [discoveringDbs, setDiscoveringDbs] = useState(false);
    const [dbDiscoveryError, setDbDiscoveryError] = useState(null);

    const [touchedFields, setTouchedFields] = useState({});
    const handleBlur = (field) => {
        setTouchedFields(prev => ({ ...prev, [field]: true }));
    };

    // [REMOVED MANUAL SSH STATES]

    // Step checklist state per tunnel direction (4 steps each)
    // Bridge test state
    const [bridgeTesting, setBridgeTesting] = useState(false);
    const [bridgeResult, setBridgeResult] = useState(null); // null | 'ok' | 'fail'
    const [bridgeMessage, setBridgeMessage] = useState('');
    // Bastion creation guide active provider tab

    // Bridge connectivity test
    const testBridgeConnection = async () => {
        setBridgeTesting(true);
        setBridgeResult(null);
        setBridgeMessage('');
        try {
            const resp = await fetch(`${API_BASE_URL}/api/dms/test-bridge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sourceId: formData.sourceId,
                    targetId: formData.targetId,
                    options: {
                        connectivityMode: 'private'
                        // Allocated ports are now managed entirely by the backend SSHTunnelManager
                    }
                })
            });
            const data = await resp.json();
            setBridgeResult(data.success ? 'ok' : 'fail');
            setBridgeMessage(data.success
                ? `Bridge active — ${data.sourceName || 'Source'} → ${data.targetName || 'Target'} reachable through tunnels`
                : (data.error || 'Tunnel not reachable. Ensure both SSH commands are running on the bastion VMs.'));
        } catch (e) {
            setBridgeResult('fail');
            setBridgeMessage(e.message || 'Network error');
        } finally {
            setBridgeTesting(false);
        }
    };

    // Sync connectivity mode with chosen connections
    React.useEffect(() => {
        if (isWizardOpen && step === 2 && formData.sourceId && formData.targetId) {
            const source = connections.find(c => String(c.id) === String(formData.sourceId));
            const target = connections.find(c => String(c.id) === String(formData.targetId));

            const isSSHSource = source?.use_ssh_tunnel || source?.ssh_config?.enabled === true || source?.ssh_config?.enabled === 'true';
            const isSSHTarget = target?.use_ssh_tunnel || target?.ssh_config?.enabled === true || target?.ssh_config?.enabled === 'true';

            if (isSSHSource || isSSHTarget) {
                setFormData(prev => ({ ...prev, connectivityMode: 'private' }));
            } else {
                setFormData(prev => ({ ...prev, connectivityMode: 'public' }));
            }
        }
    }, [isWizardOpen, step, formData.sourceId, formData.targetId, connections, setFormData]);

    // Separate active jobs and drafts
    const activeJobs = jobs ? jobs.filter(j => j.status !== 'draft') : [];
    const draftJobs = jobs ? jobs.filter(j => j.status === 'draft') : [];

    const getFilteredAndSorted = (list, isDraftsList) => {
        let result = [...list];

        // 1. Search Query Filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(job => {
                const sourceConn = connections.find(c => String(c.id) === String(job.source_connection_id));
                const targetConn = connections.find(c => String(c.id) === String(job.target_connection_id));
                const sourceName = sourceConn?.name || '';
                const targetName = targetConn?.name || '';
                return (
                    job.name?.toLowerCase().includes(q) ||
                    job.status?.toLowerCase().includes(q) ||
                    (job.job_type || job.jobType)?.toLowerCase().includes(q) ||
                    sourceName.toLowerCase().includes(q) ||
                    targetName.toLowerCase().includes(q)
                );
            });
        }

        // 2. Dropdown Filter
        if (selectedFilter !== 'all') {
            result = result.filter(job => {
                if (selectedFilter === 'homogeneous' || selectedFilter === 'heterogeneous') {
                    const sourceConn = connections.find(c => String(c.id) === String(job.source_connection_id));
                    const targetConn = connections.find(c => String(c.id) === String(job.target_connection_id));
                    if (!sourceConn || !targetConn) return false;
                    const sourceEngine = (sourceConn.db_type || sourceConn.type || '').toLowerCase();
                    const targetEngine = (targetConn.db_type || targetConn.type || '').toLowerCase();
                    const isHomo = sourceEngine === targetEngine;
                    return selectedFilter === 'homogeneous' ? isHomo : !isHomo;
                }

                // Status filters for active jobs
                if (!isDraftsList) {
                    if (selectedFilter === 'running') {
                        return job.status === 'running' || job.status === 'replicating' || job.status === 'cdc_syncing';
                    }
                    return job.status === selectedFilter;
                }

                return true;
            });
        }

        // 3. Sorting Logic
        result.sort((a, b) => {
            if (sortBy === 'a_z') {
                return (a.name || '').localeCompare(b.name || '');
            } else if (sortBy === 'recently_created') {
                const getCreateTime = (item) => {
                    if (item.created_at) return new Date(item.created_at).getTime();
                    return 0;
                };
                return getCreateTime(b) - getCreateTime(a);
            } else if (sortBy === 'recently_updated') {
                const getUpdateTime = (item) => {
                    if (item.updated_at) return new Date(item.updated_at).getTime();
                    if (item.created_at) return new Date(item.created_at).getTime();
                    return 0;
                };
                return getUpdateTime(b) - getUpdateTime(a);
            } else if (sortBy === 'status') {
                const statusA = a.status || '';
                const statusB = b.status || '';
                return statusA.localeCompare(statusB);
            }
            return 0;
        });

        return result;
    };

    const displayActiveJobs = getFilteredAndSorted(activeJobs, false);
    const displayDraftJobs = getFilteredAndSorted(draftJobs, true);
    const filteredJobs = activeTab === 'jobs' ? displayActiveJobs : displayDraftJobs;

    const handleStopJob = (jobId) => {
        setStopJobId(jobId);
        setStopModalOpen(true);
    };

    const confirmStop = async () => {
        try {
            console.log(`[DMS] Initiating stop for job ${stopJobId}...`);
            const response = await fetch(`${API_BASE_URL}/api/dms/pipelines/${stopJobId}/stop`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (data.success) {
                showNotification('✅ ' + (data.message || 'Job stop command initiated successfully'), 'success');

                // Track stopped job in localStorage
                const stoppedJobs = JSON.parse(localStorage.getItem('dms_stopped_jobs') || '[]');
                if (!stoppedJobs.includes(stopJobId)) {
                    stoppedJobs.push(stopJobId);
                    localStorage.setItem('dms_stopped_jobs', JSON.stringify(stoppedJobs));
                }

                // Refresh list immediately
                setTimeout(() => fetchJobs(), 500);
            } else {
                console.error('[DMS] Stop failed:', data.error);
                showNotification(`❌ Failed to stop job: ${data.error || 'Server error'}`, 'error');
            }
        } catch (error) {
            console.error('[DMS] Network error stopping job:', error);
            showNotification('❌ Network error: Could not connect to server to stop job.', 'error');
        } finally {
            setStopModalOpen(false);
            setStopJobId(null);
        }
    };

    const handleCutoverJob = (jobId) => {
        setCutoverJobId(jobId);
        setCutoverModalOpen(true);
    };

    const confirmCutover = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/dms/pipelines/${cutoverJobId}/cutover`, {
                method: 'POST'
            });
            const data = await response.json();
            if (data.success) {
                showNotification('✅ Cutover completed successfully! Target database is now ready for production.', 'success');
                fetchJobs();
            } else {
                showNotification(data.error || 'Failed to perform cutover', 'error');
            }
        } catch (error) {
            console.error('Error performing cutover:', error);
            showNotification('Failed to perform cutover', 'error');
        } finally {
            setCutoverModalOpen(false);
            setCutoverJobId(null);
        }
    };


    // Helper function to get step status
    const getStepStatus = (stepNumber) => {
        if (step > stepNumber) return 'completed';
        if (step === stepNumber) return 'active';
        return 'pending';
    };

    // Helper function to get step subtitle
    const getStepSubtitle = (stepNumber) => {
        switch (stepNumber) {
            case 1: return 'Basic migration information';
            case 2: return 'Select the source database';
            case 3: return 'Select the destination database';
            case 4: return 'Configure connectivity';
            case 5: return 'Choose databases to migrate';
            case 6: return 'Validate and create the migration';
            default: return '';
        }
    };

    // Helper function to get cloud provider
    const getCloudProvider = (engineCode) => {
        if (!engineCode) return null;
        if (engineCode.startsWith('aws-')) return 'aws';
        if (engineCode.startsWith('gcp-')) return 'gcp';
        if (engineCode.startsWith('azure-')) return 'azure';
        return 'on-premise';
    };

    // Helper function to get engine code from connection
    const getEngineCode = (conn) => {
        if (!conn) return '';
        const provider = conn.cloud_provider || 'on-premise';
        const type = (conn.db_type || conn.type || '').toLowerCase();

        if (provider === 'aws') return `aws-${type}`;
        if (provider === 'gcp') return `gcp-${type}`;
        if (provider === 'azure') return `azure-${type}`;
        return type; // on-premise
    };

    const getEngineLabel = (code) => {
        if (!code) return 'Not selected';
        const labels = {
            'aws-postgresql': 'Amazon RDS for PostgreSQL',
            'aws-mysql': 'Amazon RDS for MySQL',
            'gcp-postgresql': 'Cloud SQL for PostgreSQL',
            'gcp-mysql': 'Cloud SQL for MySQL',
            'azure-postgresql': 'Azure Database for PostgreSQL',
            'azure-mysql': 'Azure Database for MySQL',
            'postgresql': 'PostgreSQL (On-premise)',
            'mysql': 'MySQL (On-premise)'
        };
        return labels[code] || code;
    };



    // Smart Filtering: Get connection count for an engine
    const getConnectionCount = (engineKey) => {
        return connections.filter(conn => getEngineCode(conn) === engineKey).length;
    };


    // Validation: Check engine compatibility
    const validateEngineCompatibility = (sourceEngine, destEngine) => {
        if (!sourceEngine || !destEngine) return { valid: false };

        // Extract base engine type
        const getBaseEngine = (engine) => {
            const parts = engine.split('-');
            return parts[parts.length - 1]; // 'aws-postgresql' -> 'postgresql'
        };

        const sourceBase = getBaseEngine(sourceEngine);
        const destBase = getBaseEngine(destEngine);

        // Same source and destination
        if (sourceEngine === destEngine) {
            return { valid: false, error: 'Source and destination cannot be the same' };
        }

        // Homogeneous migration (same engine type)
        if (sourceBase === destBase) {
            return { valid: true, type: 'homogeneous', message: 'Homogeneous migration (same database engine)' };
        }

        // Heterogeneous migration compatibility matrix
        const compatibilityMatrix = {
            'mysql': ['postgresql'],
            'postgresql': ['mysql']
        };

        if (compatibilityMatrix[sourceBase]?.includes(destBase)) {
            return {
                valid: true,
                type: 'heterogeneous',
                warning: 'Heterogeneous migration - Schema conversion may be required'
            };
        }

        return { valid: false, error: 'Incompatible database engines' };
    };

    // Get current validation status
    const getValidationStatus = () => {
        if (!formData.sourceEngine || !formData.destinationEngine) return null;
        return validateEngineCompatibility(formData.sourceEngine, formData.destinationEngine);
    };

    if (isWizardOpen) {
        return (
            <div className="modal-overlay wizard-modal-overlay">
                <div className="wizard-modal">
                    {/* Wizard Header */}
                    <div className="wizard-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                                onClick={() => setIsWizardOpen(false)}
                                className="back-btn"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'var(--text-primary)'
                                }}
                            >
                                ←
                            </button>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500' }}>Create a migration job</h2>
                        </div>
                        <button onClick={() => setIsWizardOpen(false)} className="modal-close">
                            <X size={24} />
                        </button>
                    </div>

                    {/* Wizard Body with Sidebar */}
                    <div className="wizard-body">
                        {/* Left Sidebar */}
                        <div className="wizard-sidebar">
                            {/* Step 1 */}
                            <div className={`wizard-step-item ${getStepStatus(1)}`}>
                                <div className="step-number">
                                    {getStepStatus(1) === 'completed' ? '✓' : '1'}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">Overview</div>
                                    <div className="step-subtitle">{getStepSubtitle(1)}</div>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className={`wizard-step-item ${getStepStatus(2)}`}>
                                <div className="step-number">
                                    {getStepStatus(2) === 'completed' ? '✓' : '2'}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">Source</div>
                                    <div className="step-subtitle">{getStepSubtitle(2)}</div>
                                </div>
                            </div>

                            {/* Step 3 */}
                            <div className={`wizard-step-item ${getStepStatus(3)}`}>
                                <div className="step-number">
                                    {getStepStatus(3) === 'completed' ? '✓' : '3'}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">Target</div>
                                    <div className="step-subtitle">{getStepSubtitle(3)}</div>
                                </div>
                            </div>

                            {/* Step 4 */}
                            <div className={`wizard-step-item ${getStepStatus(4)}`}>
                                <div className="step-number">
                                    {getStepStatus(4) === 'completed' ? '✓' : '4'}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">Network</div>
                                    <div className="step-subtitle">{getStepSubtitle(4)}</div>
                                </div>
                            </div>

                            {/* Step 5 - Select Databases */}
                            <div className={`wizard-step-item ${getStepStatus(5)}`}>
                                <div className="step-number">
                                    {getStepStatus(5) === 'completed' ? '✓' : '5'}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">Database Selection</div>
                                    <div className="step-subtitle">{getStepSubtitle(5)}</div>
                                </div>
                            </div>

                            {/* Step 6 */}
                            <div className={`wizard-step-item ${getStepStatus(6)}`}>
                                <div className="step-number">
                                    {getStepStatus(6) === 'completed' ? '✓' : '6'}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">Review & Launch</div>
                                    <div className="step-subtitle">{getStepSubtitle(6)}</div>
                                </div>
                            </div>

                            {/* Sidebar Actions */}
                            <div className="wizard-sidebar-actions">
                                {!editContext ? (
                                    <>
                                        <button className="btn-secondary-outline" onClick={() => handleStartMigration(true)}>
                                            <Save size={16} /> Save and Exit
                                        </button>
                                        <button className="btn-ghost" onClick={() => setIsWizardOpen(false)}>
                                            Discard draft
                                        </button>
                                    </>
                                ) : (
                                    <button className="btn-ghost" onClick={() => setIsWizardOpen(false)}>
                                        Cancel Edit
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className="wizard-content">
                            {/* Step 1: Get Started */}
                            {step === 1 && (
                                <div className="wizard-step-content">
                                    <div className="step-header">
                                        <h3>Overview</h3>
                                        <p>Configure basic migration information.</p>
                                    </div>

                                    <div className="step-body">
                                        {/* Migration Job Name */}
                                        <div className="form-group">
                                            <label>
                                                Migration job name <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${formData.jobName && jobs.some(j => j.name && j.name.toLowerCase() === formData.jobName.toLowerCase()) ? 'is-invalid' : ''}`}
                                                placeholder="e.g., prod-mysql-to-postgres-migration"
                                                value={formData.jobName}
                                                onChange={e => setFormData({ ...formData, jobName: e.target.value })}
                                                onBlur={() => handleBlur('jobName')}
                                                style={{
                                                    marginTop: '0.5rem',
                                                    borderColor: ((touchedFields.jobName && (!formData.jobName || !formData.jobName.trim())) || (formData.jobName && jobs.some(j => j.name && j.name.toLowerCase() === formData.jobName.toLowerCase()))) ? '#ef4444' : 'var(--border-color)'
                                                }}
                                            />
                                            {touchedFields.jobName && (!formData.jobName || !formData.jobName.trim()) && (
                                                <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <AlertCircle size={12} />
                                                    Migration job name is required.
                                                </div>
                                            )}
                                            {formData.jobName && jobs.some(j => j.name && j.name.toLowerCase() === formData.jobName.toLowerCase()) && (
                                                <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                                    <AlertCircle size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                                    This name is already in use. Please choose a unique name.
                                                </div>
                                            )}
                                            <small style={{ color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
                                                Choose a descriptive name for this migration job
                                            </small>
                                        </div>

                                        {/* Source Database Engine */}
                                        <div className="form-group" style={{ marginTop: '1.5rem' }}>
                                            <label>Source database engine <span style={{ color: '#ef4444' }}>*</span></label>
                                            <select
                                                value={formData.sourceEngine || ''}
                                                onChange={e => {
                                                    const newSourceEngine = e.target.value;
                                                    const sourceProvider = getCloudProvider(newSourceEngine);
                                                    const targetProvider = getCloudProvider(formData.destinationEngine);

                                                    let updates = { sourceEngine: newSourceEngine };
                                                    if (sourceProvider && sourceProvider === targetProvider) {
                                                        updates.destinationEngine = '';
                                                        updates.targetId = '';
                                                    }
                                                    setFormData({ ...formData, ...updates });
                                                }}
                                                onBlur={() => handleBlur('sourceEngine')}
                                                className="form-control"
                                                style={{
                                                    marginTop: '0.5rem',
                                                    borderColor: touchedFields.sourceEngine && !formData.sourceEngine ? '#ef4444' : 'var(--border-color)'
                                                }}
                                            >
                                                <option value="">Select source engine</option>
                                                <optgroup label="AWS RDS">
                                                    <option value="aws-mysql">
                                                        Amazon RDS for MySQL {getConnectionCount('aws-mysql') > 0 ? `(${getConnectionCount('aws-mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="aws-postgresql">
                                                        Amazon RDS for PostgreSQL {getConnectionCount('aws-postgresql') > 0 ? `(${getConnectionCount('aws-postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                                <optgroup label="Google Cloud SQL">
                                                    <option value="gcp-mysql">
                                                        Cloud SQL for MySQL {getConnectionCount('gcp-mysql') > 0 ? `(${getConnectionCount('gcp-mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="gcp-postgresql">
                                                        Cloud SQL for PostgreSQL {getConnectionCount('gcp-postgresql') > 0 ? `(${getConnectionCount('gcp-postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                                <optgroup label="Azure Database">
                                                    <option value="azure-mysql">
                                                        Azure Database for MySQL {getConnectionCount('azure-mysql') > 0 ? `(${getConnectionCount('azure-mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="azure-postgresql">
                                                        Azure Database for PostgreSQL {getConnectionCount('azure-postgresql') > 0 ? `(${getConnectionCount('azure-postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                                <optgroup label="On-Premises / Self-Hosted">
                                                    <option value="mysql">
                                                        MySQL {getConnectionCount('mysql') > 0 ? `(${getConnectionCount('mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="postgresql">
                                                        PostgreSQL {getConnectionCount('postgresql') > 0 ? `(${getConnectionCount('postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                            </select>
                                            {touchedFields.sourceEngine && !formData.sourceEngine && (
                                                <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <AlertCircle size={12} />
                                                    Source database engine is required.
                                                </div>
                                            )}
                                        </div>

                                        {/* Destination Database Engine */}
                                        <div className="form-group" style={{ marginTop: '1.5rem' }}>
                                            <label>Destination database engine <span style={{ color: '#ef4444' }}>*</span></label>
                                            <select
                                                value={formData.destinationEngine || ''}
                                                onChange={e => setFormData({ ...formData, destinationEngine: e.target.value })}
                                                onBlur={() => handleBlur('destinationEngine')}
                                                className="form-control"
                                                style={{
                                                    marginTop: '0.5rem',
                                                    borderColor: touchedFields.destinationEngine && !formData.destinationEngine ? '#ef4444' : 'var(--border-color)'
                                                }}
                                            >
                                                <option value="">Select destination engine</option>
                                                <optgroup label={`AWS RDS${getCloudProvider(formData.sourceEngine) === 'aws' ? ' (Already selected as Source)' : ''}`} disabled={getCloudProvider(formData.sourceEngine) === 'aws'}>
                                                    <option value="aws-mysql">
                                                        Amazon RDS for MySQL {getConnectionCount('aws-mysql') > 0 ? `(${getConnectionCount('aws-mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="aws-postgresql">
                                                        Amazon RDS for PostgreSQL {getConnectionCount('aws-postgresql') > 0 ? `(${getConnectionCount('aws-postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                                <optgroup label={`Google Cloud SQL${getCloudProvider(formData.sourceEngine) === 'gcp' ? ' (Already selected as Source)' : ''}`} disabled={getCloudProvider(formData.sourceEngine) === 'gcp'}>
                                                    <option value="gcp-mysql">
                                                        Cloud SQL for MySQL {getConnectionCount('gcp-mysql') > 0 ? `(${getConnectionCount('gcp-mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="gcp-postgresql">
                                                        Cloud SQL for PostgreSQL {getConnectionCount('gcp-postgresql') > 0 ? `(${getConnectionCount('gcp-postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                                <optgroup label={`Azure Database${getCloudProvider(formData.sourceEngine) === 'azure' ? ' (Already selected as Source)' : ''}`} disabled={getCloudProvider(formData.sourceEngine) === 'azure'}>
                                                    <option value="azure-mysql">
                                                        Azure Database for MySQL {getConnectionCount('azure-mysql') > 0 ? `(${getConnectionCount('azure-mysql')} available)` : '(no connections)'}
                                                    </option>
                                                    <option value="azure-postgresql">
                                                        Azure Database for PostgreSQL {getConnectionCount('azure-postgresql') > 0 ? `(${getConnectionCount('azure-postgresql')} available)` : '(no connections)'}
                                                    </option>
                                                </optgroup>
                                            </select>
                                            {touchedFields.destinationEngine && !formData.destinationEngine && (
                                                <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <AlertCircle size={12} />
                                                    Destination database engine is required.
                                                </div>
                                            )}
                                            <small style={{ color: 'var(--text-muted)', marginTop: '0.4rem', display: 'block' }}>
                                                Source and Target must use different cloud providers.
                                            </small>

                                            {/* Validation Feedback */}
                                            {(() => {
                                                const validation = getValidationStatus();
                                                if (!validation) return (
                                                    <div style={{
                                                        marginTop: '1.5rem',
                                                        padding: '1.25rem',
                                                        background: '#ffffff',
                                                        borderRadius: '16px',
                                                        border: '1px solid rgba(102, 126, 234, 0.3)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '16px',
                                                        boxShadow: '0 10px 25px rgba(0,0,0,0.04)',
                                                        position: 'relative',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'linear-gradient(to bottom, #667eea, #764ba2)' }}></div>
                                                        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)' }}>
                                                            <Zap size={20} fill="white" />
                                                        </div>
                                                        <div style={{ fontSize: '0.875rem', lineHeight: '1.5', color: '#374151' }}>
                                                            <strong style={{ display: 'block', color: '#1f2937', marginBottom: '2px', fontSize: '0.95rem' }}>Production-Ready Engines</strong>
                                                            These engines are certified for high-performance replication and continuous CDC synchronization.
                                                        </div>
                                                    </div>
                                                );

                                                if (validation.error) {
                                                    return (
                                                        <div style={{
                                                            marginTop: '1rem',
                                                            padding: '1.25rem',
                                                            background: '#ffffff',
                                                            borderLeft: '4px solid #ef4444',
                                                            borderRadius: '12px',
                                                            color: '#b91c1c',
                                                            fontSize: '0.875rem',
                                                            boxShadow: '0 10px 25px rgba(239, 68, 68, 0.05)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px'
                                                        }}>
                                                            <div style={{ background: '#ef4444', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: 'bold' }}>!</div>
                                                            <span>{validation.error}</span>
                                                        </div>
                                                    );
                                                }

                                                if (validation.warning) {
                                                    return (
                                                        <div style={{
                                                            marginTop: '1rem',
                                                            padding: '1.25rem',
                                                            background: '#ffffff',
                                                            borderLeft: '4px solid #f59e0b',
                                                            borderRadius: '12px',
                                                            color: '#92400e',
                                                            fontSize: '0.875rem',
                                                            boxShadow: '0 10px 25px rgba(245, 158, 11, 0.05)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px'
                                                        }}>
                                                            <div style={{ background: '#f59e0b', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: 'bold' }}>!</div>
                                                            <span>{validation.warning}</span>
                                                        </div>
                                                    );
                                                }

                                                if (validation.message) {
                                                    return (
                                                        <div style={{
                                                            marginTop: '1rem',
                                                            padding: '1.25rem',
                                                            background: '#ffffff',
                                                            borderLeft: '4px solid #10b981',
                                                            borderRadius: '12px',
                                                            color: '#065f46',
                                                            fontSize: '0.875rem',
                                                            boxShadow: '0 10px 25px rgba(16, 185, 129, 0.05)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px'
                                                        }}>
                                                            <div style={{ background: '#10b981', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.8rem', fontWeight: 'bold' }}>✓</div>
                                                            <span>{validation.message}</span>
                                                        </div>
                                                    );
                                                }
                                            })()}
                                        </div>



                                        {/* Migration Type */}
                                        <div className="form-group" style={{ marginTop: '1.5rem' }}>
                                            <label>Migration Type <span style={{ color: '#ef4444' }}>*</span></label>
                                            <select
                                                value={formData.migrationType || ''}
                                                onChange={e => setFormData({ ...formData, migrationType: e.target.value })}
                                                onBlur={() => handleBlur('migrationType')}
                                                className="form-control"
                                                style={{
                                                    marginTop: '0.5rem',
                                                    borderColor: touchedFields.migrationType && !formData.migrationType ? '#ef4444' : 'var(--border-color)'
                                                }}
                                            >
                                                <option value="" disabled>Select migration type</option>
                                                <option value="full+cdc">Full Load + CDC - Complete Migration</option>
                                                <option value="cdc">Continuous (CDC) - Real-time Replication</option>
                                                <option value="snapshot">One-time (Snapshot) - Bulk Load</option>
                                            </select>
                                            {touchedFields.migrationType && !formData.migrationType && (
                                                <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <AlertCircle size={12} />
                                                    Migration Type is required.
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="step-footer">
                                        <button
                                            className="btn-primary"
                                            onClick={() => setStep(2)}
                                            disabled={!formData.jobName || !formData.jobName.trim() || jobs.some(j => j.name && j.name.toLowerCase() === formData.jobName.toLowerCase()) || !formData.sourceEngine || !formData.destinationEngine || !formData.migrationType}
                                        >
                                            Continue →
                                        </button>
                                    </div>
                                </div>
                            )}


                            {/* Step 2: Define Source */}
                            {step === 2 && (
                                <div className="wizard-step-content">
                                    <div className="step-header">
                                        <h3>Source</h3>
                                        <p>Configure the source database connection.</p>
                                    </div>

                                    <div className="step-body">
                                        <div className="form-group">
                                            <label>
                                                Source connection <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <select
                                                value={formData.sourceId}
                                                onChange={e => {
                                                    if (e.target.value === 'create_new') {
                                                        onCreateConnection();
                                                        return;
                                                    }
                                                    const selectedConn = connections.find(c => String(c.id) === String(e.target.value));
                                                    setFormData({
                                                        ...formData,
                                                        sourceId: e.target.value,
                                                        sourceEngine: getEngineCode(selectedConn)
                                                    });
                                                }}
                                                className="form-control"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                <option value="">Select source connection</option>
                                                <option value="create_new" style={{ fontWeight: 'bold', color: '#a855f7' }}>
                                                    + Create New Connection
                                                </option>
                                                {connections
                                                    .filter(conn => {
                                                        // If source engine is selected in Step 1, filter by that engine
                                                        if (formData.sourceEngine) {
                                                            return getEngineCode(conn) === formData.sourceEngine;
                                                        }
                                                        // Otherwise show all connections
                                                        return true;
                                                    })
                                                    .map(conn => (
                                                        <option key={conn.id} value={conn.id}>
                                                            {conn.name} ({conn.db_type || conn.type})
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                            <small style={{ color: 'var(--text-muted)', marginTop: '0.5rem', display: 'block' }}>
                                                The database you want to migrate data from
                                            </small>
                                        </div>

                                        {/* Connection details hidden - cleaner UI */}
                                    </div>

                                    <div className="step-footer">
                                        {!editContext && (
                                            <button
                                                className="btn-secondary"
                                                onClick={() => setStep(1)}
                                            >
                                                ← Back
                                            </button>
                                        )}
                                        {editContext === 'source' ? (
                                            <button
                                                className="btn-primary"
                                                onClick={() => handleStartMigration(true)}
                                                disabled={!formData.sourceId || formData.sourceId === 'create_new'}
                                            >
                                                Save & Exit
                                            </button>
                                        ) : (
                                            <button
                                                className="btn-primary"
                                                onClick={() => setStep(3)}
                                                disabled={!formData.sourceId || formData.sourceId === 'create_new'}
                                            >
                                                Continue →
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Define Destination */}
                            {step === 3 && (
                                <div className="wizard-step-content">
                                    <div className="step-header">
                                        <h3>Target</h3>
                                        <p>Configure the destination database connection.</p>
                                    </div>

                                    <div className="step-body">
                                        <div className="form-group">
                                            <label>
                                                Destination connection <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <select
                                                value={formData.targetId}
                                                onChange={e => {
                                                    if (e.target.value === 'create_new') {
                                                        onCreateConnection();
                                                        return;
                                                    }
                                                    const selectedTarget = connections.find(c => String(c.id) === String(e.target.value));
                                                    setFormData({
                                                        ...formData,
                                                        targetId: e.target.value,
                                                        destinationEngine: getEngineCode(selectedTarget)
                                                    });
                                                }}
                                                className="form-control"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                <option value="">Select destination connection</option>
                                                <option value="create_new" style={{ fontWeight: 'bold', color: '#a855f7' }}>
                                                    + Create New Connection
                                                </option>
                                                {connections
                                                    .filter(conn => {
                                                        // If destination engine is selected in Step 1, filter by that engine
                                                        if (formData.destinationEngine) {
                                                            return getEngineCode(conn) === formData.destinationEngine;
                                                        }
                                                        // Otherwise show all connections
                                                        return true;
                                                    })
                                                    .map(conn => (
                                                        <option key={conn.id} value={conn.id}>
                                                            {conn.name} ({conn.db_type || conn.type})
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                            <small style={{ color: 'var(--text-muted)', marginTop: '0.5rem', display: 'block' }}>
                                                The database you want to migrate data to
                                            </small>
                                        </div>


                                        {/* Connection details hidden - cleaner UI */}
                                    </div>

                                    <div className="step-footer">
                                        {!editContext && (
                                            <button
                                                className="btn-secondary"
                                                onClick={() => setStep(2)}
                                            >
                                                ← Back
                                            </button>
                                        )}
                                        {editContext === 'target' ? (
                                            <button
                                                className="btn-primary"
                                                onClick={() => handleStartMigration(true)}
                                                disabled={!formData.targetId || formData.targetId === 'create_new'}
                                            >
                                                Save & Exit
                                            </button>
                                        ) : (
                                            <button
                                                className="btn-primary"
                                                onClick={() => setStep(4)}
                                                disabled={!formData.targetId || formData.targetId === 'create_new'}
                                            >
                                                Continue →
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Define Connectivity Method */}
                            {step === 4 && (
                                <div className="wizard-step-content">
                                    <div className="step-header">
                                        <h3>Network</h3>
                                        <p>Configure network connectivity.</p>
                                    </div>

                                    <div className="step-body">
                                        {/* Information Banner */}
                                        <div style={{
                                            background: 'rgba(59, 130, 246, 0.1)',
                                            border: '1px solid rgba(59, 130, 246, 0.3)',
                                            borderRadius: '8px',
                                            padding: '1rem',
                                            marginBottom: '1.5rem',
                                            display: 'flex',
                                            gap: '0.75rem'
                                        }}>
                                            <div style={{ flexShrink: 0, marginTop: '2px' }}>
                                                <AlertCircle size={20} style={{ color: '#3b82f6' }} />
                                            </div>
                                            <div style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                                                <strong>Note:</strong> The destination instance creation must finish before you complete this step.
                                                This may take a few minutes. In the meantime, choose how to connect or save and exit and return later.
                                            </div>
                                        </div>



                                        {/* Connectivity Mode Selection - Premium Cards */}
                                        <div className="form-group" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                                            <label style={{ marginBottom: '1rem', display: 'block' }}>
                                                Connectivity method <span style={{ color: '#ef4444' }}>*</span>
                                            </label>

                                            <div style={{ position: 'relative' }}>
                                                <select
                                                    value={formData.connectivityMode || 'public'}
                                                    onChange={(e) => setFormData({ ...formData, connectivityMode: e.target.value })}
                                                    className="form-control"
                                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer' }}
                                                >
                                                    <option value="vpn">HA VPN Bridge</option>
                                                    <option value="private">Managed SSH</option>
                                                    <option value="public">IP Allowlist (Public)</option>
                                                </select>
                                            </div>

                                            <div style={{ marginTop: '1.5rem' }}>
                                                {formData.connectivityMode === 'vpn' && !vpnStatus.configured && (
                                                    <div style={{ padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', fontSize: '0.8rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <AlertTriangle size={16} /> No active VPN tunnel detected. Please setup Private Connectivity first.
                                                    </div>
                                                )}

                                                {formData.connectivityMode === 'vpn' && vpnStatus.configured && (
                                                    <div style={{ padding: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '8px', fontSize: '0.8rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <ShieldCheck size={16} /> Tunnel established! Data will flow securely through your AWS-to-GCP bridge.
                                                    </div>
                                                )}
                                            </div>

                                            {formData.connectivityMode === 'public' && (
                                                <div style={{ marginTop: '1.25rem', padding: '1rem', background: 'rgba(147, 51, 234, 0.05)', borderRadius: '8px', border: '1px solid rgba(147, 51, 234, 0.2)' }}>
                                                    <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <Network size={14} /> Connectivity Flow:
                                                    </h4>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.5rem 0', overflowX: 'auto' }}>
                                                        <div style={{ flex: '1', textAlign: 'center', minWidth: '80px' }}>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>DMS Engine</div>
                                                            <div style={{ padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '0.75rem' }}>Runner</div>
                                                        </div>
                                                        <ArrowRight size={14} style={{ color: 'var(--text-muted)' }} />
                                                        <div style={{ flex: '1', textAlign: 'center', minWidth: '80px' }}>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Public IP</div>
                                                            <div style={{ padding: '6px', background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.3)', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', color: '#a855f7' }}>34.47.241.76</div>
                                                        </div>
                                                        <ArrowRight size={14} style={{ color: 'var(--text-muted)' }} />
                                                        <div style={{ flex: '1', textAlign: 'center', minWidth: '80px' }}>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Firewall</div>
                                                            <div style={{ padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '0.75rem' }}>Allowlist</div>
                                                        </div>
                                                        <ArrowRight size={14} style={{ color: 'var(--text-muted)' }} />
                                                        <div style={{ flex: '1', textAlign: 'center', minWidth: '80px' }}>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Database</div>
                                                            <div style={{ padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '0.75rem' }}>Port Open</div>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '0.75rem' }}>
                                                        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', fontWeight: 'bold', color: '#a855f7' }}>Setup Steps:</h4>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                            <div style={{ display: 'flex', gap: '10px', fontSize: '0.825rem' }}>
                                                                <div style={{ width: '18px', height: '18px', minWidth: '18px', background: '#a855f7', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>1</div>
                                                                <div style={{ color: 'var(--text-muted)' }}>
                                                                    <strong>Copy Runner IP:</strong> Copy the static public IP Address shown below in the allowlist box.
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '10px', fontSize: '0.825rem' }}>
                                                                <div style={{ width: '18px', height: '18px', minWidth: '18px', background: '#a855f7', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>2</div>
                                                                <div style={{ color: 'var(--text-muted)' }}>
                                                                    <strong>Configure Firewall:</strong> Add an <strong>Inbound Rule</strong> to your source database's Security Group/Firewall for this IP and the DB port.
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '10px', fontSize: '0.825rem' }}>
                                                                <div style={{ width: '18px', height: '18px', minWidth: '18px', background: '#a855f7', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>3</div>
                                                                <div style={{ color: 'var(--text-muted)' }}>
                                                                    <strong>Verification:</strong> You will perform the final connectivity test in the <strong>Review & Launch</strong> step.
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: '0.75rem' }}>
                                                        <button
                                                            onClick={(e) => { e.preventDefault(); alert('Refer to the Connectivity Guide artifact in your brain folder for detailed setup steps.'); }}
                                                            style={{
                                                                fontSize: '0.85rem',
                                                                color: 'var(--primary-color)',
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: 0,
                                                                textDecoration: 'underline',
                                                                cursor: 'pointer',
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            View detailed connectivity guide
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>


                                        {formData.connectivityMode === 'private' && (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem', width: '100%' }}>
                                                {[
                                                    { id: formData.sourceId, label: 'Source (AWS)' },
                                                    { id: formData.targetId, label: 'Target (GCP)' }
                                                ].map(target => {
                                                    const conn = connections.find(c => String(c.id) === String(target.id));
                                                    const isSSH = conn?.ssh_config?.enabled === true || conn?.ssh_config?.enabled === 'true';
                                                    return (
                                                        <div key={target.id} style={{
                                                            padding: '0.75rem',
                                                            background: 'rgba(255,255,255,0.03)',
                                                            borderRadius: '8px',
                                                            border: '1px solid var(--border-color)',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}>
                                                            <div style={{ fontSize: '0.8rem' }}>
                                                                <div style={{ color: 'var(--text-muted)', marginBottom: '2px' }}>{target.label}</div>
                                                                <div style={{ fontWeight: '600' }}>{conn?.name || 'Unknown'}</div>
                                                            </div>
                                                            {isSSH ? (
                                                                <div style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
                                                                    <CheckCircle size={12} /> SSH Active
                                                                </div>
                                                            ) : (
                                                                <div style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
                                                                    <AlertCircle size={12} /> IP Mode
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {formData.connectivityMode === 'private' && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
                                                {[
                                                    { direction: 'source', label: 'Source Connectivity (AWS)', connId: formData.sourceId },
                                                    { direction: 'target', label: 'Destination Connectivity (GCP)', connId: formData.targetId }
                                                ].map((tunnel, idx) => {
                                                    const connection = connections.find(c => String(c.id) === String(tunnel.connId));
                                                    const isManaged = connection?.use_ssh_tunnel || connection?.ssh_config?.enabled === true || connection?.ssh_config?.enabled === 'true';
                                                    return (
                                                        <div key={idx} style={{
                                                            background: isManaged ? 'rgba(16, 185, 129, 0.03)' : 'rgba(239, 68, 68, 0.03)',
                                                            padding: '1.5rem',
                                                            borderRadius: '12px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            border: isManaged ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(239, 68, 68, 0.2)',
                                                            width: '100%'
                                                        }}>
                                                            <h4 style={{ margin: '0 0 1rem 0', color: isManaged ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
                                                                {isManaged ? <ShieldCheck size={18} /> : <AlertTriangle size={18} />}
                                                                {tunnel.label}: {isManaged ? 'Managed SSH Tunnel' : 'Configuration Required'}
                                                            </h4>

                                                            {isManaged ? (
                                                                <div style={{ padding: '1rem', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#60a5fa' }}>
                                                                        <Activity className="spin" size={18} />
                                                                        <div>
                                                                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Status: Automated Tunnel Active</div>
                                                                            <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>DMS is managing secure access to <strong>{connection?.host}</strong> via bastion <strong>{connection?.ssh_host}</strong>.</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                                                                    <div style={{ color: '#f87171', fontSize: '0.85rem', lineHeight: '1.5' }}>
                                                                        <strong>Managed SSH Tunneling is not enabled.</strong>
                                                                        <br />Private network connectivity requires Managed SSH Tunneling. Please go back to <strong>Connections</strong> and enable "Use SSH Tunnel" for this profile.
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {/* === BRIDGE TEST === */}
                                                <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: bridgeResult === 'ok' ? 'rgba(16,185,129,0.06)' : bridgeResult === 'fail' ? 'rgba(239,68,68,0.06)' : 'var(--bg-icon-wrapper)', border: `1px solid ${bridgeResult === 'ok' ? 'rgba(16,185,129,0.3)' : bridgeResult === 'fail' ? 'rgba(239,68,68,0.3)' : 'var(--border-color)'}`, borderRadius: '10px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '3px' }}>🔌 Test Tunnel Bridge</div>
                                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Verify both tunnels are active and the DMS runner can reach Source and Target through them.</div>
                                                        </div>
                                                        <button
                                                            onClick={testBridgeConnection}
                                                            disabled={bridgeTesting}
                                                            style={{ padding: '0.55rem 1.25rem', background: 'linear-gradient(135deg, #7c3aed, #10b981)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: bridgeTesting ? 'not-allowed' : 'pointer', opacity: bridgeTesting ? 0.7 : 1, whiteSpace: 'nowrap' }}
                                                        >
                                                            {bridgeTesting ? '⏳ Testing...' : '🚀 Test Bridge Connection'}
                                                        </button>
                                                    </div>
                                                    {bridgeResult && (
                                                        <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.85rem', borderRadius: '6px', background: bridgeResult === 'ok' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: bridgeResult === 'ok' ? '#10b981' : '#ef4444', fontSize: '0.83rem', fontWeight: 500 }}>
                                                            {bridgeResult === 'ok' ? '✅ ' : '❌ '}{bridgeMessage}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {formData.connectivityMode === 'public' && (
                                            <div className="connectivity-card" style={{
                                                padding: '3rem 2rem',
                                                borderRadius: '24px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                textAlign: 'center',
                                                marginTop: '1.5rem',
                                                position: 'relative'
                                            }}>
                                                <div className="runner-ip-box" style={{
                                                    marginBottom: '2rem',
                                                    padding: '1.5rem 3rem',
                                                    borderRadius: '20px',
                                                    display: 'inline-block'
                                                }}>
                                                    <small style={{ opacity: 1, textTransform: 'uppercase', display: 'block', marginBottom: '10px', fontSize: '0.8rem', letterSpacing: '1.5px', fontWeight: '700', color: '#7c3aed' }}>Runner IP to Allowlist</small>
                                                    <code style={{ fontSize: '2rem', fontWeight: '900', letterSpacing: '1px' }}>34.47.241.76</code>
                                                </div>
                                                <p style={{ margin: 0, fontSize: '1rem', color: '#4b5563', maxWidth: '480px', lineHeight: '1.6' }}>
                                                    Configure your firewall to allow inbound traffic from this IP on port <strong style={{ color: '#7c3aed' }}>{formData.sourceEngine?.includes('mysql') ? '3306' : '5432'}</strong>.
                                                    {formData.sourceEngine?.includes('postgresql') && (
                                                        <div className="info-box" style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '12px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left' }}>
                                                            <div style={{ background: '#3b82f6', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: 'bold' }}>i</div>
                                                            <span><strong>PostgreSQL Tip:</strong> Remember to update <code>pg_hba.conf</code> to allow this IP for database access.</span>
                                                        </div>
                                                    )}
                                                    {formData.sourceEngine?.includes('mysql') && (
                                                        <div className="info-box" style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '12px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left' }}>
                                                            <div style={{ background: '#f59e0b', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: 'bold' }}>i</div>
                                                            <span><strong>MySQL Tip:</strong> Ensure <code>bind-address</code> is set to <code>0.0.0.0</code> in your <code>my.cnf</code> config.</span>
                                                        </div>
                                                    )}
                                                </p>
                                            </div>
                                        )}

                                        {formData.connectivityMode === 'vpn' && (
                                            <div style={{
                                                background: 'rgba(16, 185, 129, 0.03)',
                                                padding: '2rem',
                                                borderRadius: '12px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid rgba(16, 185, 129, 0.2)',
                                                width: '100%',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌐</div>
                                                <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981' }}>Private VPC Peering</h4>
                                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)', maxWidth: '400px' }}>
                                                    Traffic will route through the established HA VPN tunnel between your AWS and GCP VPCs.
                                                    Ensure your database security groups allow traffic from the secondary VPC CIDR.
                                                </p>
                                            </div>
                                        )}

                                    </div>

                                    <div className="step-footer">
                                        <button
                                            className="btn-secondary"
                                            onClick={() => setStep(3)}
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            className="btn-primary"
                                            onClick={() => {
                                                setStep(5);
                                                // Auto-trigger database discovery when entering step 5
                                                if (formData.sourceId) {
                                                    setDiscoveringDbs(true);
                                                    setDbDiscoveryError(null);
                                                    fetch(`${API_BASE_URL}/api/dms/discover-databases`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({ connectionId: formData.sourceId })
                                                    })
                                                        .then(r => r.json())
                                                        .then(data => {
                                                            if (data.success) {
                                                                setDiscoveredDbs(data.databases);
                                                                setSelectedDatabases(data.databases); // Select all by default
                                                            } else {
                                                                setDbDiscoveryError(data.error || 'Failed to discover databases');
                                                            }
                                                        })
                                                        .catch(err => setDbDiscoveryError(err.message))
                                                        .finally(() => setDiscoveringDbs(false));
                                                }
                                            }}
                                            disabled={!formData.connectivityMode}
                                        >
                                            Continue →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 5: Select Databases */}
                            {step === 5 && (
                                <div className="wizard-step-content">
                                    <div className="step-header">
                                        <h3>Database Selection</h3>
                                        <p>Choose the databases to migrate.</p>
                                    </div>

                                    <div className="step-body">
                                        {discoveringDbs ? (
                                            <div style={{ textAlign: 'center', padding: '3rem' }}>
                                                <div className="status-spinner" style={{ width: '40px', height: '40px', borderTopColor: '#a855f7', margin: '0 auto' }}></div>
                                                <p style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>Discovering databases on source server...</p>
                                            </div>
                                        ) : dbDiscoveryError ? (
                                            <div style={{ padding: '1.5rem', background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '10px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.75rem' }}>
                                                    <AlertCircle size={20} style={{ color: '#ef4444' }} />
                                                    <strong style={{ color: '#ef4444' }}>Discovery Failed</strong>
                                                </div>
                                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)' }}>{dbDiscoveryError}</p>
                                                <button
                                                    className="btn-secondary"
                                                    onClick={() => {
                                                        setDiscoveringDbs(true);
                                                        setDbDiscoveryError(null);
                                                        fetch(`${API_BASE_URL}/api/dms/discover-databases`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ connectionId: formData.sourceId })
                                                        })
                                                            .then(r => r.json())
                                                            .then(data => {
                                                                if (data.success) {
                                                                    setDiscoveredDbs(data.databases);
                                                                    setSelectedDatabases(data.databases);
                                                                } else {
                                                                    setDbDiscoveryError(data.error || 'Failed');
                                                                }
                                                            })
                                                            .catch(err => setDbDiscoveryError(err.message))
                                                            .finally(() => setDiscoveringDbs(false));
                                                    }}
                                                    style={{ marginTop: '1rem', fontSize: '0.85rem' }}
                                                >
                                                    <RefreshCw size={14} style={{ marginRight: '6px' }} /> Retry Discovery
                                                </button>
                                            </div>
                                        ) : (
                                            <div>
                                                {/* Select All / Deselect All */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                                    <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                                        Found <strong style={{ color: 'var(--text-primary)' }}>{discoveredDbs.length}</strong> database(s) on source server
                                                    </span>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => setSelectedDatabases([...discoveredDbs])}
                                                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            Select All
                                                        </button>
                                                        <button
                                                            onClick={() => setSelectedDatabases([])}
                                                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', background: 'rgba(255,255,255,0.03)', color: 'var(--text-muted)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            Deselect All
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Database List */}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '350px', overflowY: 'auto', padding: '0.25rem' }}>
                                                    {discoveredDbs.map((dbName, idx) => {
                                                        const isSelected = selectedDatabases.includes(dbName);
                                                        return (
                                                            <label
                                                                key={idx}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '12px',
                                                                    padding: '0.85rem 1rem',
                                                                    borderRadius: '8px',
                                                                    border: isSelected ? '1px solid rgba(168, 85, 247, 0.4)' : '1px solid var(--border-color)',
                                                                    background: isSelected ? 'rgba(168, 85, 247, 0.06)' : 'var(--bg-secondary)',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.15s ease'
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => {
                                                                        setSelectedDatabases(prev =>
                                                                            prev.includes(dbName)
                                                                                ? prev.filter(d => d !== dbName)
                                                                                : [...prev, dbName]
                                                                        );
                                                                    }}
                                                                    style={{ width: '14px', height: '14px', accentColor: '#a855f7' }}
                                                                />
                                                                <Database size={16} style={{ color: isSelected ? '#a855f7' : 'var(--text-muted)', flexShrink: 0 }} />
                                                                <span style={{ fontSize: '0.95rem', fontWeight: isSelected ? '600' : '400', color: isSelected ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                                                    {dbName}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>

                                                {/* Selection summary */}
                                                <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.15)', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                                    <strong style={{ color: '#a855f7' }}>{selectedDatabases.length}</strong> of {discoveredDbs.length} databases selected for migration
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="step-footer">
                                        <button
                                            className="btn-secondary"
                                            onClick={() => setStep(4)}
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            className="btn-primary"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, selectedDatabases }));
                                                setStep(6);
                                            }}
                                            disabled={selectedDatabases.length === 0}
                                        >
                                            Continue →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 6: Test and Create Migration Job */}
                            {step === 6 && (
                                <div className="wizard-step-content">
                                    <div className="step-header">
                                        <h3>Review & Launch</h3>
                                        <p>Review the configuration and create the migration.</p>
                                    </div>

                                    <div className="step-body">

                                        <div className="wiz-summary-section">
                                            <div style={{ marginBottom: '1.5rem' }}>
                                                <h4 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '500' }}>Test and create your migration job</h4>
                                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                                                    Review the details that you entered for this migration job, and make sure that you test it before creating. You can create this job without starting it, or start it immediately.
                                                </p>
                                            </div>

                                            <div className="summary-table" style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                background: 'var(--bg-secondary)'
                                            }}>
                                                {[
                                                    { label: 'Migration job name', value: formData.jobName },
                                                    { label: 'Source database engine', value: getEngineLabel(formData.sourceEngine) },
                                                    { label: 'Destination database engine', value: getEngineLabel(formData.destinationEngine) },
                                                    { label: 'Type', value: formData.migrationType === 'cdc' ? 'Continuous' : 'One-time' },
                                                    { label: 'Connection profile display name', value: connections.find(c => String(c.id) === String(formData.sourceId))?.name || 'N/A' },
                                                    { label: 'Hostname:Port', value: connections.find(c => String(c.id) === String(formData.sourceId)) ? `${connections.find(c => String(c.id) === String(formData.sourceId)).host}:${connections.find(c => String(c.id) === String(formData.sourceId)).port}` : 'N/A' },
                                                    { label: 'Full dump method', value: 'Logical' },
                                                    { label: 'Full dump options', value: 'Auto-generated' },
                                                    { label: 'Data dump parallelism level', value: formData.parallelism === 4 ? 'Optimal' : formData.parallelism },
                                                    { label: 'Destination instance ID', value: formData.jobName || 'N/A' },
                                                    { label: 'Region', value: formData.cloudRegion },
                                                    { label: 'Connectivity method', value: formData.connectivityMode === 'public' ? 'IP allowlist' : (formData.connectivityMode === 'private' ? 'SSH tunnel' : 'VPC peering') },
                                                    { label: 'Outgoing IP address', value: '34.47.241.76' }
                                                ].map((row, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'minmax(250px, 1fr) 2fr',
                                                        padding: '0.75rem 1rem',
                                                        borderBottom: idx === 12 ? 'none' : '1px solid var(--border-color)',
                                                        fontSize: '0.9rem'
                                                    }}>
                                                        <div style={{ color: 'var(--text-muted)' }}>{row.label}</div>
                                                        <div style={{ fontWeight: '500' }}>{row.value}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div style={{ marginTop: '2.5rem' }}>
                                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '500' }}>Test the migration job</h4>
                                                <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                                    Validate connectivity across the entire data path before launching.
                                                </p>

                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(250px, 1fr) 1.5fr',
                                                    gap: '1px',
                                                    background: 'var(--border-color)',
                                                    borderRadius: '12px',
                                                    border: '1px solid var(--border-color)',
                                                    overflow: 'hidden',
                                                    minHeight: '220px'
                                                }}>
                                                    {/* Left Side: Steps */}
                                                    <div style={{ background: 'var(--bg-input)', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                                        {[
                                                            { key: 'source', label: 'Source Connectivity', icon: Database },
                                                            { key: 'target', label: 'Destination Connectivity', icon: Database },
                                                            { key: 'bridge', label: 'Replication Bridge', icon: Shuffle }
                                                        ].map((testStep, i) => {
                                                            const status = testSteps[testStep.key];
                                                            const Icon = testStep.icon;
                                                            return (
                                                                <div key={testStep.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', opacity: status === 'pending' ? 0.4 : 1 }}>
                                                                    <div style={{
                                                                        width: '28px', height: '28px', borderRadius: '6px',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        background: status === 'success' ? 'rgba(16, 185, 129, 0.15)' : status === 'error' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255,255,255,0.05)',
                                                                        color: status === 'success' ? '#10b981' : status === 'error' ? '#ef4444' : 'var(--text-primary)'
                                                                    }}>
                                                                        {status === 'loading' ? (
                                                                            <Activity size={16} className="spin" />
                                                                        ) : status === 'success' ? (
                                                                            <CheckCircle size={16} />
                                                                        ) : status === 'error' ? (
                                                                            <X size={16} />
                                                                        ) : (
                                                                            <Icon size={16} />
                                                                        )}
                                                                    </div>
                                                                    <span style={{ fontSize: '0.9rem', fontWeight: status === 'loading' ? '600' : '400' }}>{testStep.label}</span>
                                                                </div>
                                                            );
                                                        })}

                                                        {!loading && !validationResult && (
                                                            <button
                                                                className="dms-btn"
                                                                onClick={handleValidateConnection}
                                                                style={{ marginTop: 'auto', width: '100%', padding: '0.6rem' }}
                                                            >
                                                                Run All Tests
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Right Side: Feedback/Errors */}
                                                    <div style={{ background: 'var(--bg-secondary)', padding: '2rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                                                        {!loading && !validationResult ? (
                                                            <>
                                                                <Activity size={32} style={{ opacity: 0.2, marginBottom: '1rem' }} />
                                                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Click "Run All Tests" to verify configuration</p>
                                                            </>
                                                        ) : loading ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                <div className="status-spinner" style={{ width: '40px', height: '40px', borderTopColor: '#a855f7' }}></div>
                                                                <p style={{ marginTop: '1rem', fontWeight: '500' }}>Testing in progress...</p>
                                                                <p style={{ fontSize: '0.8rem', opacity: 0.6 }}>Running network diagnostics and credentials check</p>
                                                            </div>
                                                        ) : validationResult?.success ? (
                                                            <div style={{ animation: 'scaleUp 0.3s ease-out', textAlign: 'left', width: '100%' }}>
                                                                {/* CDC Error Warning Widget */}
                                                                {validationResult?.cdcErrors && validationResult.cdcErrors.length > 0 ? (
                                                                    <div style={{
                                                                        background: 'rgba(245, 158, 11, 0.08)',
                                                                        border: '1px solid rgba(245, 158, 11, 0.4)',
                                                                        borderRadius: '10px',
                                                                        padding: '1.1rem 1.25rem',
                                                                        marginBottom: '1rem'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.6rem' }}>
                                                                            <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                                                                            <strong style={{ color: '#f59e0b', fontSize: '0.9rem' }}>CDC Requirements Not Met</strong>
                                                                        </div>
                                                                        <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.82rem', color: 'rgba(245,158,11,0.85)' }}>
                                                                            The source database is not configured for Change Data Capture. Fix the following before launching:
                                                                        </p>
                                                                        <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                            {validationResult.cdcErrors.map((e, i) => (
                                                                                <li key={i} style={{ fontSize: '0.81rem', color: '#fcd34d', fontFamily: 'monospace' }}>{e}</li>
                                                                            ))}
                                                                        </ul>
                                                                        <p style={{ margin: '0.6rem 0 1rem 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                                            For AWS RDS: enable <code>rds.logical_replication</code> in your RDS Parameter Group and reboot the instance.
                                                                        </p>
                                                                        <button
                                                                            className="btn-secondary"
                                                                            onClick={handleValidateConnection}
                                                                            style={{ fontSize: '0.82rem', padding: '0.4rem 1rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}
                                                                        >
                                                                            <RefreshCw size={14} style={{ marginRight: '8px' }} /> Re-run Validation
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ textAlign: 'center' }}>
                                                                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                                                                            <CheckCircle size={32} />
                                                                        </div>
                                                                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981' }}>Validation Successful</h4>
                                                                        <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>All connection points passed. You are ready to launch.</p>
                                                                        <button
                                                                            className="btn-secondary"
                                                                            onClick={handleValidateConnection}
                                                                            style={{ fontSize: '0.82rem', padding: '0.4rem 1rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10b981' }}
                                                                        >
                                                                            <RefreshCw size={14} style={{ marginRight: '8px' }} /> Re-run Validation
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div style={{ textAlign: 'left', width: '100%' }}>
                                                                <div style={{ display: 'flex', alignItems: 'start', gap: '12px', marginBottom: '1.25rem' }}>
                                                                    <div style={{ color: '#ef4444', marginTop: '3px' }}><AlertCircle size={20} /></div>
                                                                    <div>
                                                                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#ef4444' }}>Test Failed</h4>
                                                                        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>Issue detected during <strong>{validationResult?.stage || 'test'}</strong> stage.</p>
                                                                    </div>
                                                                </div>

                                                                {/* Reason Section */}
                                                                <div style={{ marginBottom: '1.25rem' }}>
                                                                    <small style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>Technical Reason:</small>
                                                                    <div style={{
                                                                        background: 'rgba(239, 68, 68, 0.05)',
                                                                        border: '1px solid rgba(239, 68, 68, 0.2)',
                                                                        padding: '0.85rem', borderRadius: '8px',
                                                                        fontSize: '0.825rem', color: '#fca5a5',
                                                                        fontFamily: 'monospace',
                                                                        lineHeight: '1.4'
                                                                    }}>
                                                                        {validationResult?.error || 'Unknown network error occurred.'}
                                                                    </div>
                                                                </div>

                                                                {/* Solutions Section */}
                                                                {((validationResult?.suggestions && validationResult.suggestions.length > 0) || (validationResult?.recommendations && validationResult.recommendations.length > 0) || (validationResult?.error?.includes('pg_hba.conf'))) ? (
                                                                    <div style={{ marginBottom: '1.5rem' }}>
                                                                        <small style={{ color: '#10b981', textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 'bold', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>Recommended Solutions:</small>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                            {(validationResult.suggestions || validationResult.recommendations || []).map((s, idx) => (
                                                                                <div key={idx} style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                                                                                    <div style={{ marginTop: '5px', width: '6px', height: '6px', minWidth: '6px', borderRadius: '50%', background: '#10b981', opacity: 0.6 }}></div>
                                                                                    {s.replace('{ip}', serverIP || 'Runner IP')}
                                                                                </div>
                                                                            ))}
                                                                            {validationResult?.error?.includes('pg_hba.conf') && (
                                                                                <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                                                                                    <div style={{ marginTop: '5px', width: '6px', height: '6px', minWidth: '6px', borderRadius: '50%', background: '#10b981', opacity: 0.6 }}></div>
                                                                                    <div>
                                                                                        Add this entry to your <code style={{ color: '#a855f7' }}>pg_hba.conf</code>: <br />
                                                                                        <code style={{ background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: '4px', color: '#10b981', display: 'inline-block', marginTop: '4px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                                                                            host all all {serverIP}/32 md5
                                                                                        </code>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ marginBottom: '1.5rem' }}>
                                                                        <small style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Generic Advice:</small>
                                                                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                                                            Check your network firewall, security groups, and database credentials.
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                <button
                                                                    className="btn-secondary"
                                                                    onClick={handleValidateConnection}
                                                                    style={{ fontSize: '0.85rem', padding: '0.5rem 1.25rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)' }}
                                                                >
                                                                    <RefreshCw size={14} style={{ marginRight: '8px' }} /> Retry Diagnostics
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="step-footer">
                                        <button
                                            className="btn-secondary"
                                            onClick={() => setStep(5)}
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            className="btn-primary"
                                            onClick={() => handleStartMigration(false)}
                                            disabled={!validationResult?.success || (validationResult?.cdcErrors && validationResult.cdcErrors.length > 0)}
                                            title={validationResult?.cdcErrors?.length > 0 ? 'Fix CDC requirements before launching' : ''}
                                            style={{
                                                background: (validationResult?.success && !(validationResult?.cdcErrors?.length > 0))
                                                    ? 'linear-gradient(135deg, #a855f7, #7c3aed)'
                                                    : 'var(--bg-input)',
                                                color: (validationResult?.success && !(validationResult?.cdcErrors?.length > 0)) ? 'white' : 'var(--text-muted)',
                                                cursor: (validationResult?.success && !(validationResult?.cdcErrors?.length > 0)) ? 'pointer' : 'not-allowed',
                                                opacity: (validationResult?.success && !(validationResult?.cdcErrors?.length > 0)) ? 1 : 0.5
                                            }}
                                        >
                                            Launch Job <Sparkles size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div >
        );
    }

    // Select All Logic
    const filteredIds = filteredJobs.map(j => j.id);
    const isAllSelected = filteredIds.length > 0 && filteredIds.every(id => selectedJobs.includes(id));

    const toggleSelectAll = () => {
        handleSelectMultipleJobs(filteredIds, !isAllSelected);
    };

    const filterOptions = activeTab === 'jobs'
        ? [
            { value: 'all', label: 'All Migration Jobs' },
            { value: 'running', label: 'Running' },
            { value: 'completed', label: 'Completed' },
            { value: 'stopped', label: 'Stopped' },
            { value: 'failed', label: 'Failed' },
            { value: 'homogeneous', label: 'Homogeneous' },
            { value: 'heterogeneous', label: 'Heterogeneous' }
        ]
        : [
            { value: 'all', label: 'All Drafts' },
            { value: 'homogeneous', label: 'Homogeneous' },
            { value: 'heterogeneous', label: 'Heterogeneous' }
        ];

    const sortOptions = activeTab === 'jobs'
        ? [
            { value: 'recently_created', label: 'Recently Created' },
            { value: 'recently_updated', label: 'Recently Updated' },
            { value: 'a_z', label: 'Alphabetical (A-Z)' },
            { value: 'status', label: 'Sort by Status' }
        ]
        : [
            { value: 'recently_created', label: 'Recently Created' },
            { value: 'recently_updated', label: 'Recently Updated' },
            { value: 'a_z', label: 'Alphabetical (A-Z)' }
        ];

    const activeFilterLabel = filterOptions.find(o => o.value === selectedFilter)?.label || 'Filter';
    const activeSortLabel = sortOptions.find(o => o.value === sortBy)?.label || 'Sort';

    return (
        <div className="dms-page-container dms-connections-container">
            {/* Action Toolbar */}
            <div className="dms-page-header">
                <div className="header-title-stack">
                    <h2>Migration Jobs</h2>
                    <p className="header-subtitle">Create, manage, and track your database migration workflows in real-time.</p>
                </div>
                <div className="header-actions-premium">
                    <div className="toolbar-buttons" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {selectedJobs.length > 0 && (
                            <button
                                className="btn-delete-selected-nexus"
                                onClick={() => onDeleteJobs(selectedJobs)}
                            >
                                <Trash2 size={18} />
                                <span>Delete Selected ({selectedJobs.length})</span>
                            </button>
                        )}
                        <button
                            className="btn-add-nexus"
                            onClick={() => setIsWizardOpen(true)}
                        >
                            <Plus size={18} />
                            <span>Create migration job</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Toolbar: Search, Filter, Sort + Toggles */}
            <div className="connections-toolbar-premium">
                <div className="search-bar-glass-container">
                    <Search size={16} className="search-icon-dim" />
                    <input
                        type="text"
                        className="search-input-glass"
                        placeholder={activeTab === 'jobs' ? "Search migration jobs..." : "Search drafts..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="clear-search-btn" onClick={() => setSearchQuery('')}>&times;</button>
                    )}
                </div>

                <div className="view-switcher-premium">
                    <button
                        className={`btn-switcher-tab ${activeTab === 'jobs' ? 'active' : ''}`}
                        onClick={() => handleTabChange('jobs')}
                    >
                        Migration Jobs
                    </button>
                    <button
                        className={`btn-switcher-tab ${activeTab === 'drafts' ? 'active' : ''} ${draftJobs.length > 0 ? 'has-drafts' : ''}`}
                        onClick={() => handleTabChange('drafts')}
                    >
                        Drafts {draftJobs.length > 0 ? `• ${draftJobs.length}` : ''}
                    </button>
                </div>

                <div className="toolbar-actions-group">
                    {/* Filter Dropdown */}
                    <div className="toolbar-dropdown-container">
                        <button
                            className={`btn-toolbar-dropdown ${selectedFilter !== 'all' ? 'active-filter' : ''}`}
                            onClick={() => {
                                setIsFilterDropdownOpen(!isFilterDropdownOpen);
                                setIsSortDropdownOpen(false);
                            }}
                        >
                            <SlidersHorizontal size={13} />
                            <span>{activeFilterLabel}</span>
                            <ChevronDown size={13} className={`chevron-rotate ${isFilterDropdownOpen ? 'open' : ''}`} />
                        </button>

                        <div className={`dropdown-panel-glass float-menu ${isFilterDropdownOpen ? 'open' : ''}`}>
                            <div className="dropdown-panel-header">Filter by</div>
                            <div className="dropdown-panel-options">
                                {filterOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dropdown-option-btn ${selectedFilter === opt.value ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedFilter(opt.value);
                                            setIsFilterDropdownOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {selectedFilter === opt.value && <Check size={13} className="check-icon-purple" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="toolbar-dropdown-container">
                        <button
                            className="btn-toolbar-dropdown"
                            onClick={() => {
                                setIsSortDropdownOpen(!isSortDropdownOpen);
                                setIsFilterDropdownOpen(false);
                            }}
                        >
                            <ArrowUpDown size={13} />
                            <span>{activeSortLabel}</span>
                            <ChevronDown size={13} className={`chevron-rotate ${isSortDropdownOpen ? 'open' : ''}`} />
                        </button>

                        <div className={`dropdown-panel-glass float-menu ${isSortDropdownOpen ? 'open' : ''}`}>
                            <div className="dropdown-panel-header">Sort by</div>
                            <div className="dropdown-panel-options">
                                {sortOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dropdown-option-btn ${sortBy === opt.value ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSortBy(opt.value);
                                            setIsSortDropdownOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {sortBy === opt.value && <Check size={13} className="check-icon-purple" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className={`dms-table-container ${isTransitioning ? 'transition-blur' : ''}`}>
                <table className="dms-table">
                    <thead>
                        <tr>
                            <th className="checkbox-col">
                                <input
                                    type="checkbox"
                                    className="dms-checkbox"
                                    checked={isAllSelected}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th>Migration Job Name</th>
                            <th>Status</th>
                            <th>Phase</th>
                            <th>Source Connection</th>
                            <th>Destination Connection</th>
                            <th className="actions-col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredJobs.length === 0 ? (
                            <tr><td colSpan="8" className="empty-cell">No {activeTab} found.</td></tr>
                        ) : filteredJobs.map(job => {
                            const sourceConn = connections.find(c => String(c.id) === String(job.source_connection_id));
                            const targetConn = connections.find(c => String(c.id) === String(job.target_connection_id));
                            const sourceName = sourceConn?.name || 'Unknown';
                            const targetName = targetConn?.name || 'Unknown';
                            return (
                                <tr key={job.id} className={selectedJobs.includes(job.id) ? 'selected' : ''}>
                                    <td className="checkbox-col">
                                        <input
                                            type="checkbox"
                                            className="dms-checkbox"
                                            checked={selectedJobs.includes(job.id)}
                                            onChange={() => toggleJobSelection(job.id)}
                                        />
                                    </td>
                                    <td>
                                        <div
                                            className="job-name-link"
                                            onClick={() => {
                                                setSelectedJobForModal(job);
                                            }}
                                        >
                                            {job.name}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="status-cell-wrapper">
                                            <span className={`status-badge ${job.status}`}>{job.status}</span>
                                            {job.status === 'running' && (
                                                <div className="mini-progress-track">
                                                    <div
                                                        className="mini-progress-fill"
                                                        style={{ width: `${job.progress || 0}%` }}
                                                    ></div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="phase-cell">
                                            {job.status === 'running' ? (
                                                job.job_type === 'cdc' ? (
                                                    <span className="phase-text replication">
                                                        <Zap size={12} style={{ marginRight: '4px' }} />
                                                        Phase 2: Syncing (CDC)
                                                    </span>
                                                ) : (
                                                    <span className="phase-text loading">
                                                        <Activity size={12} style={{ marginRight: '4px' }} />
                                                        Phase 1: Full Load ({job.progress}%)
                                                        {job.speed_bps > 0 && (
                                                            <span className="speed-badge">
                                                                • {(parseInt(job.speed_bps) / (1024 * 1024)).toFixed(1)} MB/s
                                                            </span>
                                                        )}
                                                    </span>
                                                )
                                            ) : job.status === 'pending' ? (
                                                <span className="phase-text pending">Initializing...</span>
                                            ) : job.status === 'stopped' ? (
                                                <span className="phase-text stopped" style={{ color: '#94a3b8' }}>Migration Stopped</span>
                                            ) : job.status === 'completed' ? (
                                                <span className="phase-text success">Migration Complete</span>
                                            ) : '-'}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="connection-cell-wrapper">
                                            <span className="connection-name">{sourceName}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="connection-cell-wrapper">
                                            <span className="connection-name">{targetName}</span>
                                        </div>
                                    </td>
                                    <td className="actions-cell">
                                        <div className="action-stack">
                                            {(job.status === 'running' || job.status === 'replicating' || job.status === 'cdc_syncing') && (
                                                <button
                                                    className="icon-btn-action stop"
                                                    onClick={() => handleStopJob(job.id)}
                                                    title="Stop Migration"
                                                    style={{ color: '#ef4444' }}
                                                >
                                                    <Square size={12} fill="#ef4444" />
                                                    <span>STOP</span>
                                                </button>
                                            )}
                                            {(job.status === 'replicating' || job.status === 'cdc_syncing') && (
                                                <button
                                                    className="icon-btn-action cutover"
                                                    onClick={() => handleCutoverJob(job.id)}
                                                    title="Cutover to Target Database"
                                                    style={{ color: '#10b981' }}
                                                >
                                                    <Zap size={12} />
                                                    <span>CUTOVER</span>
                                                </button>
                                            )}
                                            {job.status === 'draft' && (
                                                <>
                                                    <button className="icon-btn-action edit" onClick={() => onEditDraft(job, 'source')} title="Edit Source">
                                                        <Edit2 size={12} />
                                                        <span>EDIT SOURCE</span>
                                                    </button>
                                                    <button className="icon-btn-action edit" onClick={() => onEditDraft(job, 'target')} title="Edit Target">
                                                        <Edit2 size={12} />
                                                        <span>EDIT TARGET</span>
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                className="icon-btn-action delete"
                                                onClick={() => onDeleteJobs([job.id])}
                                                title="Delete Job"
                                            >
                                                <Trash2 size={12} />
                                                <span>DELETE</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Stop Confirmation Modal */}
            {stopModalOpen && (
                <div className="modal-overlay" onClick={() => { setStopModalOpen(false); setStopJobId(null); }}>
                    <div className="modal-content stop-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon danger">
                                <AlertTriangle size={24} />
                            </div>
                            <h3>Confirm Stop Migration</h3>
                            <button className="modal-close" onClick={() => { setStopModalOpen(false); setStopJobId(null); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="warning-text">⚠️ Are you sure you want to stop this migration job?</p>
                            <p className="danger-text">⚠️ Stopping the migration will halt the replication process immediately. You can restart it later, but any in-flight changes will be paused.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => { setStopModalOpen(false); setStopJobId(null); }}>
                                Cancel
                            </button>
                            <button className="btn-danger" onClick={confirmStop}>
                                Stop Job
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cutover Confirmation Modal */}
            {cutoverModalOpen && (
                <div className="modal-overlay" onClick={() => { setCutoverModalOpen(false); setCutoverJobId(null); }}>
                    <div className="modal-content cutover-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon warning">
                                <AlertCircle size={24} />
                            </div>
                            <h3>Confirm Cutover</h3>
                            <button className="modal-close" onClick={() => { setCutoverModalOpen(false); setCutoverJobId(null); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="warning-text">⚠️ Are you sure you want to perform cutover? This will:</p>
                            <ol className="cutover-steps">
                                <li>Stop CDC replication</li>
                                <li>Drop the replication slot</li>
                                <li>Mark the job as completed</li>
                                <li>Make the target database ready for production</li>
                            </ol>
                            <p className="danger-text">⚠️ This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => { setCutoverModalOpen(false); setCutoverJobId(null); }}>
                                Cancel
                            </button>
                            <button className="btn-danger" onClick={confirmCutover}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {selectedJobForModal && (
                <MigrationDetailsModal
                    job={selectedJobForModal}
                    connections={connections}
                    onClose={() => setSelectedJobForModal(null)}
                />
            )}

            {/* ... modas ... */}
        </div>
    );
};

export default DMSMigrations;
